import classNames from 'classnames/bind';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus';
import {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { bindActionCreators } from 'redux';

import permissionsDecorator from '../../../../decorators/permissionsDecorator';
import withComponentName from '../../../../decorators/withComponentName';
import windowWidthDecorator from '../../../../decorators/windowWidthDecorator';
import withRouter from '../../../../decorators/withRouter';

import WatchlistManageContent from './WLManageContent';
import WatchlistUpdateSuggestion from '../../../SubscriptionsComponents/WatchlistUpdateSuggestion';
import NameWatchlistModal from '../NameWatchlistModal';
import SearchbarInner from '../../../SearchbarComponents/SearchbarInner';
import Tooltip from '../../../Tooltip';
import Preloader from '../../../Preloader';

import * as searchActions from '../../../../actions/searchActions';
import * as watchlistActions from '../../../../actions/watchlistActions';

import { formatTopicsQuery } from '../../../../helpers/watchlistHelperFunctions';
import { createGuestWatchlist } from '../../../../helpers/guestUsersWatchlists';
import { createNewPrivateCompaniesForWatchlist } from '../../../../helpers/topicsApiHelpers';

import { BASIC_SEARCH, searchTypeListWatchlistMapping } from '../../../../data/searchbar/types';

import Styles from './styles.module.scss';
import { CF_URL_FOR_FINPROMPT } from '../../../../data/environment';
import useFinprompt from '../../../../providers/FinpromptProvider/useFinprompt';

const WatchlistManageBody = (props) => {
  const {
    piwikEnabled, windowWidth, manageWlPage, onboarding,
  } = props;
  const cx = classNames.bind(Styles);

  const { isFinpromptPages } = useFinprompt();

  const dispatch = useDispatch();
  const actions = useMemo(() => (
    bindActionCreators({
      ...searchActions,
      ...watchlistActions,
    }, dispatch)
  ), [dispatch]);

  const {
    watchlistsList,
    maxWatchlists,
    newWatchlistTopics,
    searchBarActive,
    token,
    activeSearchType,
    blurSearchbar,
    openedManageWatchlistsCreateSearchbar,
    userKeywords,
    activateFilingsPreloader,
    width,
  } = useSelector(({
    watchlistReducer, subscriptions, searchReducers, common,
  }) => ({
    watchlistsList: watchlistReducer.watchlistsList,
    maxWatchlists: subscriptions.permissions.max_watchlists,
    newWatchlistTopics: (
      searchReducers[
        searchTypeListWatchlistMapping[searchReducers.activeSearchType]
      ]
    ),
    searchBarActive: searchReducers.searchBarActive,
    token: watchlistReducer.userToken,
    activeSearchType: searchReducers.activeSearchType,
    blurSearchbar: searchReducers.blur,
    openedManageWatchlistsCreateSearchbar: searchReducers.openedManageWatchlistsCreateSearchbar,
    userKeywords: searchReducers.userKeywords,
    activateFilingsPreloader: watchlistReducer.activateFilingsPreloader,
    width: common.width,
  }));

  const [isOpenSearch, setIsOpenSearch] = useState(false);
  const [isSearchBarOpened, setIsSearchBarOpened] = useState(false);

  const [watchlistTopics, setWatchlistTopics] = useState([]);
  const [userKeywordsValue, setUserKeywordsValue] = useState('');

  const [nameStep, setNameStep] = useState(false);

  const firstUpdate = useRef(true);

  const resetSearchbarValues = () => {
    actions.removeSearchbarResults();
    actions.addKeyword('');
    actions.setQuery('');
  };

  const handleConfirmNameWl = async (watchlistName) => {
    if (!(watchlistTopics?.length || userKeywordsValue?.length)) return;

    let newWatchlistTopics = watchlistTopics;
    if (activeSearchType === BASIC_SEARCH || !token) {
      newWatchlistTopics = await createNewPrivateCompaniesForWatchlist(watchlistTopics);
    }
    const msToRun = 2000; // 2 seconds
    const t0 = performance.now();
    actions.newWatchlistCreationBegin();
    if (token) {
      try {
        const query = formatTopicsQuery(newWatchlistTopics, userKeywordsValue);
        if (newWatchlistTopics?.length || userKeywordsValue) {
          await actions.createNewWatchlist(query, token, watchlistName);
        }
      } catch (error) {
        console.log(error);
      }
      // create watchlist for guest user
    } else {
      await createGuestWatchlist({
        topics: newWatchlistTopics,
        userQuery: userKeywordsValue,
        watchlistName,
        activeSearchType,
      })
        .catch((err) => {
          console.log(err);
        })
        .finally(actions.guestUserWatchlists);
    }

    if (performance.now() - t0 < msToRun) {
      await new Promise((resolve) => setTimeout(() => resolve(), performance.now() - t0));
    }

    if (onboarding) resetSearchbarValues();
    setWatchlistTopics([]);
    setUserKeywordsValue('');
    setNameStep(false);

    actions.addKeyword('');
    actions.setQuery('');
    actions.newWatchlistCreationSuccess();
  };

  const mobileSearchBar = cx('mobile_search_container', {
    opened: isOpenSearch,
  });

  const toggleSearchBar = (newState, e = null) => {
    if (e) e?.stopPropagation();
    if (width < 1024) window.scrollTo({ top: 0, behavior: 'smooth' });

    setIsSearchBarOpened(newState);
    setIsOpenSearch(newState);
  };

  const handleBackButton = () => {
    setNameStep(false);
    toggleSearchBar(true);
  };

  const handleSearchbarSubmit = ({
    keyword,
    newWatchlistTopics,
  }) => {
    setWatchlistTopics(newWatchlistTopics);
    setUserKeywordsValue(keyword);

    toggleSearchBar(false);
    setNameStep(true);
  };

  const disabledBtn = watchlistsList?.length >= maxWatchlists;

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    actions.focusSearchbar(isSearchBarOpened, true);
    actions.blurSearchbar(isSearchBarOpened);
    actions.toogleManageWatchlistsCreateSearchbar(isSearchBarOpened);
    if (isSearchBarOpened === false) {
      // remove classname 'searchbar-overflow-custom'
      document.body.classList.remove(document.body.classList[0]);
      actions.resetTabsDetails();
    }
  }, [isSearchBarOpened]);

  useEffect(() => {
    if (newWatchlistTopics?.length) setWatchlistTopics(newWatchlistTopics);
  }, [newWatchlistTopics]);

  useEffect(() => {
    if (userKeywords?.length) setUserKeywordsValue(userKeywords);
  }, [userKeywords]);

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    if (!openedManageWatchlistsCreateSearchbar) {
      setIsSearchBarOpened(openedManageWatchlistsCreateSearchbar);
    }
  }, [openedManageWatchlistsCreateSearchbar]);

  return (
    <div
      className={cx('wl-manage__wrapper', { onboarding })}
    >
      <h1 className={cx('wl-manage__title')}>Manage Watchlists</h1>
      <Tooltip
        className={cx('wl-manage__create-wl-btn--tooltip',
          { 'manage-wl-page': manageWlPage }, { hide: !disabledBtn })}
        icon={(
          <div
            onClick={(e) => toggleSearchBar(true, e)}
            className={cx('wl-manage__create-wl-btn', { disabled: disabledBtn })}
          >
            <FontAwesomeIcon icon={faPlus} />
            <span>
              Create New
            </span>
          </div>
            )}
        hasBigScreenDesign
        isBigScreen
      >
        Your current plan allows&nbsp;
        {maxWatchlists}
      &nbsp;watchlists,
        <br />
        5 topics per watchlist.&nbsp;
        <a
          href={isFinpromptPages ? `${CF_URL_FOR_FINPROMPT}/pricing` : '/pricing'}
          target={isFinpromptPages ? '_blank' : undefined}
          rel="noreferrer"
        >
          Upgrade
        </a>
      &nbsp;for more.
      </Tooltip>
      <div>
        {(searchBarActive && blurSearchbar
        && openedManageWatchlistsCreateSearchbar && windowWidth > 767) && (
          <div
            className={cx('searchbar_container', { searchbar_active: searchBarActive })}
          >
            <SearchbarInner
              searchStyles={[
                'homepage-searchbar',
                'multiple_slide_search',
                'fixed_searchbar',
                'onboarding',
                'manage_watchlists_page',
              ]}
              onSubmitManageWlPage={handleSearchbarSubmit}
              multipleSearch
              submitAddText
              placeholderNone
              manageWatchlistsPage
              hasBigScreenDesign
              searchbarType="manageWatchlistsSearchbar"
              piwikEnabled={piwikEnabled}
              isWatchlist
            />
          </div>
        )}
        {(searchBarActive && blurSearchbar
        && openedManageWatchlistsCreateSearchbar && windowWidth <= 767 && windowWidth >= 320) && (
        <div
          className={cx('searchbar_container', { searchbar_active: searchBarActive })}
        >
          <div
            className={mobileSearchBar}
          >
            <SearchbarInner
              searchStyles={[
                'homepage-searchbar',
                'multiple_slide_search',
                'fixed_searchbar',
                'onboarding',
                'manage_watchlists_page',
              ]}
              multipleSearch
              submitAddText
              placeholderNone
              manageWatchlistsPage
              onSubmitManageWlPage={handleSearchbarSubmit}
              searchbarType="manageWatchlistsSearchbar"
              piwikEnabled={piwikEnabled}
              isWatchlist
            />
          </div>
        </div>
        )}
      </div>
      <NameWatchlistModal
        openModal={nameStep}
        handleConfirmNameWl={handleConfirmNameWl}
        watchlistsList={watchlistsList}
        handleCloseModal={setNameStep}
        handleBackButton={handleBackButton}
        hasBigScreenDesign
      />
      <div className={cx('wl-manage__content')}>
        {activateFilingsPreloader ? <Preloader centered loading static /> : (
          <>
            {watchlistsList?.length ? <WatchlistManageContent onboarding={onboarding} /> : null}
            {!onboarding && (
              <WatchlistUpdateSuggestion
                bigScreen
                manageWatchlists
                piwikEnabled={piwikEnabled}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default withComponentName(
  (permissionsDecorator(windowWidthDecorator(withRouter(WatchlistManageBody)))),
);
